import React, { useState, useEffect, useCallback } from "react";
import fordStyles from "./GlobalNavBar.module.scss";
import lincolnStyles from "./GlobalNavBar-Lincoln.module.scss";
import { AppBar, Grid, Select, Toolbar, styled } from "@mui/material";
import { Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import { invalidateSessionAndRedirect } from "../../common/InvalidateSession";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import HasPermissionTo from "../../common/HasPermissionTo";
import { DEALER_SETUP, RESOURCE_CENTER } from "../../common/Constants";
import { ofType } from "../../common/PubSub";
import LincolnLogo from "../../common/LincolnLogo";
import {
  GET_DEALER_NAME,
  IS_CANADA_MARKET,
  IS_FORD_BRAND,
  IS_USA_MARKET,
} from "../../utils/EmpUtil";
import FordLogo from "../../common/FordLogo";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import { FORD_BRAND, LINCOLN_BRAND } from "../../components/accessories/Brands";

const fullHeight = {
  minHeight: "70px",
  height: "100%",
};

const StyledMenu = styled(Menu)({
  "& .MuiPaper-root": {
    border: "1px solid #d3d4d5",
  },
});

const StyledMenuItem = styled(MenuItem)({
  paddingLeft: "10px",
  paddingRight: "10px",
  color: "#102b4e",
});

const LincolnStyledMenuItem = styled(MenuItem)({
  paddingLeft: "10px",
  paddingRight: "10px",
  color: "#324047",
});

const FordSelect = styled(Select)({
  width: 250,
  textAlign: "left",
  borderRadius: 3,
  border: "solid 1px #4d4d4d",
  fontSize: 14,
  lineHeight: 1.43,
  letterSpacing: 1,
  color: "#102b4e",
});

const LincolnSelect = styled(Select)({
  width: 250,
  textAlign: "left",
  borderRadius: 3,
  border: "solid 1px #4d4d4d",
  fontSize: 14,
  lineHeight: 1.43,
  letterSpacing: 1,
  color: "#324047",
});

function GlobalNavBar({ user, setPadding, changeBrand, urlPath, isNsc, history, t }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [isFordBrand, setIsFordBrand] = useState(IS_FORD_BRAND(user.brand));
  const [anchorElPortal, setAnchorElPortal] = useState(null);

  const styles = isFordBrand ? fordStyles : lincolnStyles;

  useEffect(() => {
    const subscription = ofType("brandChanged").subscribe(() => {
      updateBrand();
    });

    const headerHeight = document.getElementById("appHeader")?.clientHeight;
    if (headerHeight) {
      setPadding(headerHeight);
    }

    return () => {
      subscription?.unsubscribe?.();
    };
  }, [setPadding]);

  const updateBrand = useCallback(() => {
    setIsFordBrand(IS_FORD_BRAND(user.brand));
  }, [user.brand]);

  const handleLogoutClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleLogoutClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleLogoutDialogOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleLogoutDialogClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleBrandChange = useCallback((event) => {
    changeBrand(event.target.value);
  }, [changeBrand]);

  const handlePortalMenuClick = useCallback((event) => {
    setAnchorElPortal(event.currentTarget);
  }, []);

  const handlePortalMenuClose = useCallback(() => {
    setAnchorElPortal(null);
  }, []);

  const isActive = useCallback((currentUrl) => {
    if (urlPath === currentUrl) {
      return styles.active;
    } else if (
      (currentUrl === "/dealer/customer-reservations" ||
        currentUrl === "/customer-handling") &&
      (urlPath?.includes("customer-reservations/order-details") ||
        urlPath?.includes("customer-reservations/purchase-details"))
    ) {
      return styles.active;
    } else if (
      urlPath &&
      urlPath.startsWith("/pricing") &&
      currentUrl === "/pricing"
    ) {
      return styles.active;
    }
    return "";
  }, [urlPath, styles]);

  const updateSubNavTabs = useCallback(() => {
    if (!isNsc && user) {
      user.updateBusinessSettingsTab?.(0);
      user.updateDashboardTab?.(0);
    }
  }, [isNsc, user]);

  const getLink = useCallback((url, displayText) => {
    return (
      <Link
        className={isActive(url)}
        onClick={updateSubNavTabs}
        to={{ pathname: url }}
      >
        {t(displayText)}
      </Link>
    );
  }, [isActive, updateSubNavTabs, t]);

  const name = GET_DEALER_NAME(user, user.market)
    ? " (" + GET_DEALER_NAME(user, user.market) + ")"
    : "";

  return (
    <AppBar
      position="fixed"
      classes={{ root: styles.globalNavBar }}
      id="appHeader"
      data-testid="appHeader"
    >
      <Toolbar style={fullHeight} disableGutters>
        <Grid container style={fullHeight}>
          <Grid item md={12} className={styles.gridItem}>
            <div className={styles.languageContainer}>
              {isFordBrand ? (
                <FordLogo className={styles.brandLogo} />
              ) : (
                <LincolnLogo className={styles.brandLogo} />
              )}
            </div>
            <div className={styles.navBarWrapper}>
              <HasPermissionTo
                perform={["dashBoardPage"]}
                permissions={user.permissions.rolePermissions}
                render={() => getLink("/dashboard", "GlobalNavBar.dashboard")}
              />
              <HasPermissionTo
                perform={["pricingPage"]}
                permissions={user.permissions.rolePermissions}
                render={() => getLink("/pricing", "GlobalNavBar.pricing")}
              />
              <HasPermissionTo
                perform={["accessoriesPage"]}
                condition={!IS_CANADA_MARKET(user.market)}
                permissions={user.permissions.rolePermissions}
                render={() =>
                  getLink("/pricing/accessories", "GlobalNavBar.accessoriesPricing")
                }
              />
              <HasPermissionTo
                perform={["customerHandlingPage"]}
                permissions={user.permissions.rolePermissions}
                render={() =>
                  getLink(
                    IS_CANADA_MARKET(user.market)
                      ? "/customer-handling/reservations_submitted"
                      : "/customer-handling/orders_requiring_attention",
                    "GlobalNavBar.customerHandling"
                  )
                }
              />
              <HasPermissionTo
                perform={["ordersPage"]}
                permissions={user.permissions.rolePermissions}
                render={() =>
                  getLink("/dealer/customer-reservations", "GlobalNavBar.orders")
                }
              />
              <HasPermissionTo
                perform={["businessSettingsPage"]}
                permissions={user.permissions.rolePermissions}
                render={() =>
                  getLink("/business-settings", "GlobalNavBar.settings")
                }
              />
              <HasPermissionTo
                perform={["reportingPage"]}
                permissions={user.permissions.rolePermissions}
                render={() => getLink("/reporting", "GlobalNavBar.reporting")}
              />
              <HasPermissionTo
                perform={["searchLandingPage"]}
                permissions={user.permissions.rolePermissions}
                render={() => getLink("/search", "Search")}
              />
              <HasPermissionTo
                perform={["poReportingPage"]}
                permissions={user.permissions.rolePermissions}
                render={() => getLink("/", "GlobalNavBar.reporting")}
              />

              <HasPermissionTo
                perform={[
                  "dealerReporting:view",
                  "dealerSetupLink",
                  "resourceCenterLink",
                ]}
                permissions={user.permissions.rolePermissions}
                condition={IS_USA_MARKET(user.market)}
                render={() => (
                  <div
                    onClick={handlePortalMenuClick}
                    className={styles.portalMenu}
                  >
                    {t("GlobalNavBar.portalText")}
                    {!anchorElPortal ? (
                      <KeyboardArrowDown />
                    ) : (
                      <KeyboardArrowUp />
                    )}
                  </div>
                )}
              />

              <Menu
                className={styles.userSection}
                id="portal-menu"
                anchorEl={anchorElPortal}
                keepMounted
                open={Boolean(anchorElPortal)}
                onClose={handlePortalMenuClose}
                onClick={handlePortalMenuClose}
                disableAutoFocusItem={true}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <HasPermissionTo
                  perform={["dealerReporting:view"]}
                  permissions={user.permissions.rolePermissions}
                  condition={IS_USA_MARKET(user.market)}
                  render={() => (
                    <MenuItem
                      onClick={() => history.push("/dealer-reporting")}
                      className={styles.logoutFont}
                    >
                      {t("GlobalNavBar.reporting")}
                    </MenuItem>
                  )}
                />

                <HasPermissionTo
                  perform={["dealerSetupLink"]}
                  permissions={user.permissions.rolePermissions}
                  render={() => (
                    <a
                      target="_blank"
                      href={DEALER_SETUP}
                      className={styles.logoutFont}
                      rel="noreferrer"
                    >
                      <MenuItem className={styles.logoutFont}>
                        {t("GlobalNavBar.dealerSetup")}
                      </MenuItem>
                    </a>
                  )}
                />

                <HasPermissionTo
                  perform={["resourceCenterLink"]}
                  permissions={user.permissions.rolePermissions}
                  render={() => (
                    <a
                      target="_blank"
                      href={RESOURCE_CENTER}
                      className={styles.logoutFont}
                      rel="noreferrer"
                    >
                      <MenuItem className={styles.logoutFont}>
                        {t("GlobalNavBar.resourceCenter")}
                      </MenuItem>
                    </a>
                  )}
                />
              </Menu>
            </div>
            <div className={styles.flex} />
            <HasPermissionTo
              perform={["franchiseSelector"]}
              permissions={user.permissions.rolePermissions}
              condition={
                IS_USA_MARKET(user.market) &&
                user.dealerGroup &&
                user.dealerGroup.length > 1
              }
              render={() => (
                <ChangeBrand
                  styles={styles}
                  brand={user.brand}
                  handleBrandChange={handleBrandChange}
                  isFordBrand={isFordBrand}
                  t={t}
                />
              )}
            />
            <Logout
              styles={styles}
              handleLogoutClick={handleLogoutClick}
              user={user}
              name={name}
              anchorEl={anchorEl}
              handleLogoutClose={handleLogoutClose}
              handleLogoutDialogOpen={handleLogoutDialogOpen}
              open={open}
              handleLogoutDialogClose={handleLogoutDialogClose}
              t={t}
            />
          </Grid>
        </Grid>
      </Toolbar>
    </AppBar>
  );
}

function Logout({
                  styles,
                  handleLogoutClick,
                  user,
                  name,
                  anchorEl,
                  handleLogoutClose,
                  handleLogoutDialogOpen,
                  open,
                  handleLogoutDialogClose,
                  t,
                }) {
  return (
    <div>
      <div className={styles.dealerContainer}>
        <div
          className={`${styles.dealerBox} ${styles.withPointer}`}
          onClick={handleLogoutClick}
        >
          <HasPermissionTo
            perform={["NADealerDetails:view"]}
            permissions={user.permissions.rolePermissions}
            condition={user.userType === "na_dealer"}
            render={() => (
              <NADealerDetails
                user={user}
                styles={styles}
                name={name}
                t={t}
              />
            )}
            noRender={() => (
              <DealerDetails user={user} name={name} styles={styles} />
            )}
          />
          <KeyboardArrowDown id="logout-menu" className={styles.arrow} />
        </div>
      </div>
      <StyledMenu
        className={styles.userSection}
        id="customized-menu"
        disableAutoFocusItem
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleLogoutClose}
      >
        <StyledMenuItem onClick={handleLogoutDialogOpen} to={{ pathname: "/" }}>
          <span className={styles.logoutFont}>{t("Logout.logout")}</span>
        </StyledMenuItem>
      </StyledMenu>

      <Dialog
        open={open}
        onClose={handleLogoutDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <span className={styles.logoutConfirmationFont}>
              {t("Logout.confirmation")}
            </span>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutDialogClose} color="primary">
            {t("Logout.decline")}
          </Button>
          <Button
            onClick={invalidateSessionAndRedirect}
            color="primary"
            autoFocus
          >
            {t("Logout.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

function DealerDetails({ user, name }) {
  return (
    <div id="name">
      {user.fullName}
      {user.userType === "dealer" && name}
    </div>
  );
}

function NADealerDetails({ user, styles, t }) {
  const meta = user.commonDealerId.split("|");
  return (
    <div className={styles.dealerDetails} id="name">
      <span className={styles.dealerType}>
        {t("GlobalNavBar.dealer")}
      </span>
      <span className={styles.dealerName}>{user.dealerName}</span>
      <span className={styles.dealerCommonId}>
        {meta[1].concat(" | ").concat(meta[2])}
      </span>
    </div>
  );
}

function ChangeBrand({ styles, brand, handleBrandChange, isFordBrand, t }) {
  const StyledSelectComponent = isFordBrand ? FordSelect : LincolnSelect;
  const StyledMenuComponent = isFordBrand ? StyledMenuItem : LincolnStyledMenuItem;

  return (
    <StyledSelectComponent
      disableUnderline
      variant="outlined"
      value={brand}
      onChange={handleBrandChange}
      data-testid="change-brand"
      inputProps={{
        "data-testid": "actual-brand",
      }}
      IconComponent={() => (
        <KeyboardArrowDown className={styles.brandIcon} />
      )}
    >
      <StyledMenuComponent value={FORD_BRAND} className={styles.arrow}>
        &nbsp;&nbsp;{t("GlobalNavBar.fordBrand")}
      </StyledMenuComponent>
      <StyledMenuComponent value={LINCOLN_BRAND} className={styles.arrow}>
        &nbsp;&nbsp;{t("GlobalNavBar.lincolnBrand")}
      </StyledMenuComponent>
    </StyledSelectComponent>
  );
}

export default withTranslation("emp")(GlobalNavBar);
